import React, { useState } from 'react';

/**
 * OnboardingModal - Welcome screen for new users (SAT-84)
 *
 * Shows a multi-step onboarding experience:
 * 1. Welcome/Product Overview
 * 2. Quick Tips
 * 3. Example Prompts
 *
 * Uses localStorage to track if user has completed onboarding.
 */
const OnboardingModal = ({ onComplete }) => {
  // Read localStorage synchronously in the initializer so the modal is open
  // on the first render — otherwise HomePage flashes before the effect fires.
  const [isOpen, setIsOpen] = useState(() => !localStorage.getItem('hasSeenOnboarding'));
  const [currentStep, setCurrentStep] = useState(0);

  const handleComplete = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setIsOpen(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setIsOpen(false);
    if (onComplete) onComplete();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    {
      title: 'Welcome to DP',
      icon: (
        <div className="w-20 h-20 bg-linear-to-br from-decisiv-blue to-amber-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
      ),
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Your AI Assistant
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            DP is here to help dealership personnel manage operations, analyze data, and streamline workflows.
            Let's take a quick tour of what you can do.
          </p>
          <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl mb-1">
                <svg className="w-6 h-6 inline-block text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10M4 18h6" />
                </svg>
              </div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Workflows</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl mb-1">{'📝'}</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Draft Content</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl mb-1">{'📊'}</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Analyze Data</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl mb-1">{'💡'}</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Brainstorm</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Quick Tips',
      icon: (
        <div className="w-20 h-20 bg-linear-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
      ),
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Get the Most Out of DP
          </h2>
          <div className="space-y-4 text-left max-w-md mx-auto">
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="w-8 h-8 bg-decisiv-blue/20 rounded-full flex items-center justify-center shrink-0">
                <span className="text-decisiv-blue font-bold">1</span>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Be specific</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Include context and details for better responses</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="w-8 h-8 bg-decisiv-blue/20 rounded-full flex items-center justify-center shrink-0">
                <span className="text-decisiv-blue font-bold">2</span>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Ask follow-ups</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Continue the conversation to refine answers</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="w-8 h-8 bg-decisiv-blue/20 rounded-full flex items-center justify-center shrink-0">
                <span className="text-decisiv-blue font-bold">3</span>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Use keyboard shortcuts</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Press <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded-sm text-xs">Cmd+K</kbd> for quick actions</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Try These Prompts',
      icon: (
        <div className="w-20 h-20 bg-linear-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
      ),
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Ready to Start?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Here are some example prompts to get you going:
          </p>
          <div className="space-y-3 text-left max-w-md mx-auto">
            <button className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left group">
              <div className="flex items-center gap-3">
                <span className="text-lg">
                  <svg className="w-5 h-5 inline-block text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </span>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white group-hover:text-decisiv-blue transition-colors">
                    "Analyze shop productivity for last week"
                  </div>
                </div>
              </div>
            </button>
            <button className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left group">
              <div className="flex items-center gap-3">
                <span className="text-lg">{'📄'}</span>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white group-hover:text-decisiv-blue transition-colors">
                    "Summarize this document and highlight key points"
                  </div>
                </div>
              </div>
            </button>
            <button className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left group">
              <div className="flex items-center gap-3">
                <span className="text-lg">{'💡'}</span>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white group-hover:text-decisiv-blue transition-colors">
                    "Brainstorm creative ideas for..."
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      )
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-xs"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-decisiv-blue transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Step indicator */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-6 bg-decisiv-blue'
                    : index < currentStep
                      ? 'bg-decisiv-blue/50'
                      : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className="flex justify-center">
            {steps[currentStep].icon}
          </div>

          {/* Step content */}
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm font-medium transition-colors"
          >
            Skip tour
          </button>

          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-decisiv-blue text-white rounded-lg hover:bg-decisiv-blue/90 transition-colors font-medium"
            >
              {currentStep === steps.length - 1 ? "Get Started" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
